import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { apiEndPoints } from '../remote/apiEndPoints';
import {
  APP_SECRET_KEY,
  extractErrorMessage,
  showErrorMessage,
  showSuccessMessage,
} from '../constants';
import { retrieveFromLocalStorage } from '../constants';
import CryptoJS from 'crypto-js';

const initialState = {
  users: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  selectedProject: null,
  images: [],
  imageDetails: [],
  keypairs: [],
  virtualAccount: [],
  securityGroups: [],
  creditHistories: [],
  serverSnapshots: [],
  volumeSnapshots: [],
  paymentHistories: [],
  invoices: [],
  userResourceSubscriptions: [],
  currency: localStorage.getItem('currency') || 'NGN',
  ...(() => {
    const persistedState = retrieveFromLocalStorage([
      'userSessionData',
      'userProjectList',
      'selectedProject',
    ]);
    return {
      ...persistedState,
      userProjectList: persistedState.userProjectList || [],
      userSessionData: persistedState.userSessionData || {},
      isAuthenticated: !!persistedState.userSessionData,
    };
  })(),
};

const saveToLocalStorage = (key, data) => {
  const encryptedData = CryptoJS.AES.encrypt(data, APP_SECRET_KEY).toString();
  localStorage.setItem(key, encryptedData);
};
const removeFromLocalStorage = (key) => {
  localStorage.removeItem(key);
};

export const registerUser = createAsyncThunk(
  'user/registration',
  async (userData, { rejectWithValue }) => {
    try {
      const userRegistrationAPI = await apiEndPoints.userRegistration(userData);
      const response = await userRegistrationAPI.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createUserVirtualAccount = createAsyncThunk(
  'user/createVirtualAccount',
  async (userData, { rejectWithValue }) => {
    try {
      const virtualAccountAPI =
        await apiEndPoints.createVirtualAccount(userData);
      const response = await virtualAccountAPI.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createSessionVirtualAccount = createAsyncThunk(
  'user/createSessionVirtualAccount',
  async (userData, { rejectWithValue }) => {
    try {
      const sessionVirtualAccountAPI =
        await apiEndPoints.createSessionVirtualAccount(userData);
      const response = await sessionVirtualAccountAPI.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const getUserVirtualAccount = createAsyncThunk(
  'user/getUserVirtualAccount',
  async () => {
    try {
      const apiGetVirtualAccount = await apiEndPoints.getVirtualAccount();

      const response = (await apiGetVirtualAccount.data) || [];
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const getCreditValidator = createAsyncThunk(
  'user/getCreditValidator',
  async ({ amount }) => {
    try {
      const apiGetCreditValidator = await apiEndPoints.validateCredit(amount);

      const response = (await apiGetCreditValidator.data) || [];
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const userAuthenticate = createAsyncThunk(
  'user/userLogin',
  async (userData, { rejectWithValue }) => {
    try {
      const apiUserLogin = await apiEndPoints.userSignIn(userData);
      const response = apiUserLogin.data;

      saveToLocalStorage('userSessionData', JSON.stringify(response));

      return response;
    } catch (error) {
      let errorMessage =
        'Something went wrong. Please try again.';

      if (!error.response) {
        errorMessage =
          'Unable to connect to CloudVantage Student Portal. Please check your internet connection and try again.';
      } else {
        errorMessage =
          error.response?.data?.statusmessage ||
          error.response?.data?.message ||
          'An unexpected error occurred.';
      }

      return rejectWithValue({
        statuscode: error.response?.status || 'NETWORK_ERROR',
        statusmessage: errorMessage,
      });
    }
  }
);
export const listDews = createAsyncThunk(
  'user/ListDews',
  async ({ navigate } = {}, { rejectWithValue }) => {
    try {
      const apiListDews = await apiEndPoints.listFlavours();
      const response = await apiListDews.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data, navigate });
    }
  },
);

export const listPartnerDews = createAsyncThunk(
  'user/ListPartners',
  async (data, { rejectWithValue }) => {
    try {
      const apiListPartners = await apiEndPoints.listPartnerFlavours(data);
      const response = await apiListPartners.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const listUserProjects = createAsyncThunk(
  'user/ListUserProjects',
  async (_, { dispatch }) => {
    try {
      const apiListUserProjects = await apiEndPoints.listUserProjects();
      const response = await apiListUserProjects.data;
      saveToLocalStorage('userProjectList', JSON.stringify(response.projects));
      if (response.projects && response.projects.length > 0) {
        const firstProject = response.projects[0];
        dispatch(setSelectedProject(firstProject));
      }
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const updateUserProject = createAsyncThunk(
  'user/updateProject',
  async ({ updateProjectData, navigate }, { rejectWithValue }) => {
    try {
      const apiUpdateProject =
        await apiEndPoints.updateUserProjects(updateProjectData);
      const response = await apiUpdateProject.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data, navigate });
    }
  },
);

export const updateResourceSubMode = createAsyncThunk(
  'user/updateResourceSubMode',
  async ({ resourceId, subModeData, navigate }, { rejectWithValue }) => {
    try {
      const apiUpdateSubMode = await apiEndPoints.updateResourceSubMode(
        resourceId,
        subModeData,
      );
      const response = await apiUpdateSubMode.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data, navigate });
    }
  },
);

export const createProject = createAsyncThunk(
  'user/CreateProject',
  async ({ createProjectData, navigate }, { rejectWithValue }) => {
    try {
      const apiCreateProject =
        await apiEndPoints.createProject(createProjectData);
      const response = await apiCreateProject.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data, navigate });
    }
  },
);

export const createServerSnapshot = createAsyncThunk(
  'user/createSnapshot',
  async ({ snapshotData, navigate }, { rejectWithValue }) => {
    try {
      const apiCreateSnapshot = await apiEndPoints.createSnapshot(snapshotData);
      const response = await apiCreateSnapshot.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data, navigate });
    }
  },
);

export const createVolumeSnapshot = createAsyncThunk(
  'user/createVolumeSnapshot',
  async ({ snapshotData, navigate }, { rejectWithValue }) => {
    try {
      const apiCreateVolumeSnapshot =
        await apiEndPoints.createSnapshotVolume(snapshotData);
      const response = await apiCreateVolumeSnapshot.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data, navigate });
    }
  },
);

export const createVolumeFromSnapshot = createAsyncThunk(
  'user/createVolumeFromSnapshot',
  async ({ volumeData, navigate }, { rejectWithValue }) => {
    try {
      const apiCreateVolumeSnapshot =
        await apiEndPoints.createSnapshotForVolume(volumeData);
      const response = await apiCreateVolumeSnapshot.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data, navigate });
    }
  },
);

export const createBackup = createAsyncThunk(
  'user/createBackup',
  async ({ snapshotData, navigate }, { rejectWithValue }) => {
    try {
      const apiCreateVolumeBackup =
        await apiEndPoints.createBackupVolume(snapshotData);
      const response = await apiCreateVolumeBackup.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data, navigate });
    }
  },
);

export const resizeServer = createAsyncThunk(
  'user/resizeServer',
  async ({ resizeServerData, navigate }, { rejectWithValue }) => {
    try {
      const apiResizeServer = await apiEndPoints.resizeServer(resizeServerData);
      const response = await apiResizeServer.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data, navigate });
    }
  },
);

export const confirmResizeServer = createAsyncThunk(
  'user/confirmResizeServer',
  async ({ confirmResizeServerData, navigate }, { rejectWithValue }) => {
    try {
      const apiConfirmResizeServer = await apiEndPoints.confirmResizeServer(
        confirmResizeServerData,
      );
      const response = await apiConfirmResizeServer.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data, navigate });
    }
  },
);

export const stopResizeServer = createAsyncThunk(
  'user/stopResizeServer',
  async ({ stopResizeServerData, navigate }, { rejectWithValue }) => {
    try {
      const apiStopResizeServer =
        await apiEndPoints.stopResizeServer(stopResizeServerData);
      const response = await apiStopResizeServer.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data, navigate });
    }
  },
);

export const listImages = createAsyncThunk('user/ListImages', async () => {
  try {
    const apiListImages = await apiEndPoints.listImages();
    const response = await apiListImages.data.images;
    return response;
  } catch (error) {
    return error.response.data;
  }
});

export const listKeypairs = createAsyncThunk(
  'user/ListKeypairs',
  async (projectId) => {
    try {
      const apiListKeypairs = await apiEndPoints.listKeypairs(projectId);
      const response = (await apiListKeypairs.data?.keypairs) || [];
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listServers = createAsyncThunk(
  'user/ListServers',
  async (projectId) => {
    try {
      const apiListKeypairs = await apiEndPoints.listServers(projectId);
      const response = (await apiListKeypairs.data?.servers) || [];
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listDBServers = createAsyncThunk(
  'user/ListServers',
  async (projectId) => {
    try {
      const apiListKeypairs = await apiEndPoints.listServersDB(projectId);
      const response = (await apiListKeypairs.data?.servers) || [];
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listProjectFloatingIPs = createAsyncThunk(
  'user/ListProjectFloatingIPs',
  async (projectId) => {
    try {
      const apiListProjectFloatingIPs =
        await apiEndPoints.listFloatingIps(projectId);
      const response =
        (await apiListProjectFloatingIPs.data?.floatingips) || [];
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listServer = createAsyncThunk(
  'user/ListServer',
  async ({ projectId, serverId }) => {
    try {
      const apiListServer = await apiEndPoints.getServer(projectId, serverId);
      const response = await apiListServer.data.server;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const getDBServerStatus = createAsyncThunk(
  'user/GetDBServerStatus',
  async ({ projectId, serverId, serverIp }) => {
    try {
      const apiGetDBServerStatus = await apiEndPoints.getDBServerStatus(
        projectId,
        serverId,
        serverIp,
      );
      return apiGetDBServerStatus.data;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const getMigrationStatus = createAsyncThunk(
  'user/GetMigrationStatus',
  async ({ projectId, taskId }) => {
    try {
      const apiGetMigrationStatus = await apiEndPoints.getMigrationStatus(
        projectId,
        taskId,
      );
      return apiGetMigrationStatus.data;
    } catch (error) {
      return error.response?.data || error;
    }
  },
);

export const listFloatingIP = createAsyncThunk(
  'user/ListFloatingIP',
  async ({ projectId, floatingIPId }) => {
    try {
      const apiListFloatingIP = await apiEndPoints.getFloatingIPs(
        projectId,
        floatingIPId,
      );
      const response = await apiListFloatingIP.data.floatingip;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

// Partners
export const listPartnerResources = createAsyncThunk(
  'user/ListPartnerResources',
  async (data) => {
    try {
      const apiListPartnerResources =
        await apiEndPoints.listPartnerResources(data);
      const response = await apiListPartnerResources.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listPartnerWallet = createAsyncThunk(
  'user/ListPartnerWallet',
  async (data) => {
    try {
      const apiListPartnerWallet = await apiEndPoints.listPartnerWallet(data);
      const response = await apiListPartnerWallet.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listPartnerPriceLists = createAsyncThunk(
  'user/ListPartnerPriceLists',
  async (data) => {
    try {
      const apiListPartnerPriceLists =
        await apiEndPoints.listPartnerPriceLists(data);
      const response = await apiListPartnerPriceLists.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listNetwork = createAsyncThunk(
  'user/ListNetwork',
  async ({ projectId, networkId }) => {
    try {
      const apiListNetwork = await apiEndPoints.getNetwork(
        projectId,
        networkId,
      );
      const response = await apiListNetwork.data.network;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listLoadBalancer = createAsyncThunk(
  'user/listLoadBalancer',
  async ({ projectId, loadBalancerId }) => {
    try {
      const apiListLoadBalancer = await apiEndPoints.getLoadBalancer(
        projectId,
        loadBalancerId,
      );
      const response = await apiListLoadBalancer.data?.['load balancer'];
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listLoadListener = createAsyncThunk(
  'user/listLoadListener',
  async ({ projectId, listenerId }) => {
    try {
      const apiListListener = await apiEndPoints.getListener(
        projectId,
        listenerId,
      );
      const response = await apiListListener.data?.listener;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listLoadPool = createAsyncThunk(
  'user/listLoadPool',
  async ({ projectId, poolId }) => {
    try {
      const apiListPool = await apiEndPoints.getPool(projectId, poolId);
      const response = await apiListPool.data?.pool;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listNetworkSubnet = createAsyncThunk(
  'user/ListNetworkSubnet',
  async ({ projectId, data }) => {
    try {
      const apiListNetworkSubnet = await apiEndPoints.getNetworkSubnet(
        projectId,
        data,
      );
      const response = await apiListNetworkSubnet.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listRouter = createAsyncThunk(
  'user/ListRouter',
  async ({ projectId, routerId }) => {
    try {
      const apiListRouter = await apiEndPoints.getRouter(projectId, routerId);
      const response = await apiListRouter.data.router;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listServerSnapshots = createAsyncThunk(
  'user/ListServerSnapshots',
  async (projectId) => {
    try {
      const apiListKeypairs = await apiEndPoints.listServerSnapshots(projectId);
      const response = (await apiListKeypairs.data) || [];
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listVolumeSnapshots = createAsyncThunk(
  'user/ListVolumeSnapshots',
  async (projectId) => {
    try {
      const apiListVolumeSnapshots =
        await apiEndPoints.listVolumeSnapshots(projectId);
      const response = (await apiListVolumeSnapshots.data?.snapshots) || [];
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listVolumeBackups = createAsyncThunk(
  'user/ListVolumeBackups',
  async (projectId) => {
    try {
      const apiListVolumeBackups =
        await apiEndPoints.listVolumeBackups(projectId);
      const response = (await apiListVolumeBackups.data?.backups) || [];
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const listImageDetails = createAsyncThunk(
  'user/ListLocalImages',
  async () => {
    try {
      const apiListLocalImageDetails =
        await apiEndPoints.listLocalImageDetails();
      const response = (await apiListLocalImageDetails.data?.images) || [];
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const switchUserProject = createAsyncThunk(
  'user/SwitchUserProject',
  async (projectId) => {
    try {
      const apiSwitchProject = await apiEndPoints.switchProject(projectId);
      const response = await apiSwitchProject.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const createKeypair = createAsyncThunk(
  'user/CreateKeypair',
  async ({ createKeypairData, projectId }, { rejectWithValue }) => {
    try {
      const apiCreateKeypair = await apiEndPoints.createKeypair(
        createKeypairData,
        projectId,
      );
      const response = await apiCreateKeypair.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createServer = createAsyncThunk(
  'user/CreateServer',
  async ({ createServerData, projectId }, { rejectWithValue }) => {
    try {
      const apiCreateServer = await apiEndPoints.createServer(
        createServerData,
        projectId,
      );
      const response = await apiCreateServer.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const setPartnerFlavorPrices = createAsyncThunk(
  'user/setPartnerFlavorPrices',
  async ({ flavorData, flavorId }, { rejectWithValue }) => {
    try {
      const apiPartnerFlavorPrices = await apiEndPoints.setPartnerFlavorPrice(
        flavorData,
        flavorId,
      );
      const response = await apiPartnerFlavorPrices.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createSecurityGroup = createAsyncThunk(
  'user/CreateSecurityGroup',
  async ({ securityGroupData, projectId }, { rejectWithValue }) => {
    try {
      const apiCreateSecurityGroup = await apiEndPoints.createSecurityGroup(
        securityGroupData,
        projectId,
      );
      const response = await apiCreateSecurityGroup.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createSecurityGroupRule = createAsyncThunk(
  'user/CreateSecurityGroupRule',
  async (
    { securityGroupRuleData, projectId, securityGroupId },
    { rejectWithValue },
  ) => {
    try {
      const apiCreateSecurityGroupRule =
        await apiEndPoints.createSecurityGroupRule(
          securityGroupRuleData,
          projectId,
          securityGroupId,
        );
      const response = await apiCreateSecurityGroupRule.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const deleteServer = createAsyncThunk(
  'user/DeleteServer',
  async ({ projectId, serverId }, { rejectWithValue }) => {
    try {
      const response = await apiEndPoints.deleteServer(projectId, serverId);
      // Return serializable data
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const listSecurityGroups = createAsyncThunk(
  'user/ListSecurityGroups',
  async (projectId) => {
    try {
      const apiListSecurityGroups =
        await apiEndPoints.listSecurityGroups(projectId);
      const response = await apiListSecurityGroups.data?.security_groups;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deleteSecurityGroup = createAsyncThunk(
  'user/DeleteSecurityGroup',
  async ({ projectId, securityGroupId }, { rejectWithValue }) => {
    try {
      const response = await apiEndPoints.deleteSecurityGroup(
        projectId,
        securityGroupId,
      );

      // Return only serializable data to avoid Redux warnings
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deleteSecurityGroupRule = createAsyncThunk(
  'user/DeleteSecurityGroupRule',
  async ({ projectId, securityGroupRuleId }, { rejectWithValue }) => {
    try {
      const response = await apiEndPoints.deleteSecurityGroupRule(
        projectId,
        securityGroupRuleId,
      );

      // Return only serializable data to avoid Redux warnings
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return error.response.data;
    }
  },
);

export const getSingleSecurityGroup = createAsyncThunk(
  'user/SingleSecurityGroup',
  async ({ projectId, securityGroupId }, { rejectWithValue }) => {
    try {
      const response = await apiEndPoints.getSingleSecurityGroups(
        projectId,
        securityGroupId,
      );

      // Return only serializable data to avoid Redux warnings
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return error.response.data;
    }
  },
);

export const createSecurityGroupInstance = createAsyncThunk(
  'user/SecurityGroupIntancce',
  async ({ instanceData, projectId }, { rejectWithValue }) => {
    try {
      const response = await apiEndPoints.createAttachSecurityGroupInstance(
        instanceData,
        projectId,
      );

      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const removeSecurityGroupInstance = createAsyncThunk(
  'user/RemoveSecurityGroupInstance',
  async ({ instanceData, projectId }, { rejectWithValue }) => {
    try {
      const response = await apiEndPoints.removeSecurityGroupInstance(
        instanceData,
        projectId,
      );

      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const getResourceStatus = createAsyncThunk(
  'user/ResourceStatus',
  async ({ serverId }, { rejectWithValue }) => {
    try {
      const response = await apiEndPoints.getResourceStatus(serverId);
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const userLogOut = createAsyncThunk('user/Logout', async () => {
  try {
    const apiUserLogout = await apiEndPoints.userSignOut();
    const response = await apiUserLogout.data;
    logOutSession();
    return response;
  } catch (error) {
    return error.response.data;
  }
});

export const createContactSupport = createAsyncThunk(
  'user/CreateContactSupport',
  async ({ createContactSupportData }, { rejectWithValue }) => {
    try {
      const apiCreateContactSupport = await apiEndPoints.createContactSupport(
        createContactSupportData,
      );
      const response = await apiCreateContactSupport.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const deleteUserProject = createAsyncThunk(
  'user/deleteUserProject',
  async (projectId) => {
    try {
      const apiDeleteProject = await apiEndPoints.deleteProject(projectId);
      const response = await apiDeleteProject.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deleteUserServer = createAsyncThunk(
  'user/deleteUserServer',
  async ({ projectId, serverId }) => {
    try {
      const apiDeleteServer = await apiEndPoints.deleteServer(
        projectId,
        serverId,
      );
      const response = await apiDeleteServer.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deleteProjectNetwork = createAsyncThunk(
  'user/deleteProjectNetwork',
  async ({ projectId, networkId }) => {
    try {
      const apiDeleteProjectNetwork = await apiEndPoints.deleteNetwork(
        projectId,
        networkId,
      );
      const response = await apiDeleteProjectNetwork.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deleteProjectRouter = createAsyncThunk(
  'user/deleteProjectRouter',
  async ({ projectId, routerId }) => {
    try {
      const apiDeleteProjectRouter = await apiEndPoints.deleteRouter(
        projectId,
        routerId,
      );
      const response = await apiDeleteProjectRouter.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deleteSubnet = createAsyncThunk(
  'user/deleteSubnet',
  async ({ projectId, subnetId }) => {
    try {
      const apiDeleteSubnet = await apiEndPoints.deleteNetworkSubnet(
        projectId,
        subnetId,
      );
      const response = await apiDeleteSubnet.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deleteProjectLoadBalancer = createAsyncThunk(
  'user/deleteProjectLoadBalancer',
  async ({ projectId, loadBalancerId }) => {
    try {
      const apiDeleteLoadBalancer = await apiEndPoints.deleteLoadBalancer(
        projectId,
        loadBalancerId,
      );
      const response = await apiDeleteLoadBalancer.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deleteListener = createAsyncThunk(
  'user/deleteListener',
  async ({ projectId, listenerId }) => {
    try {
      const apiDeleteListener = await apiEndPoints.deleteLoadListener(
        projectId,
        listenerId,
      );
      const response = await apiDeleteListener.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deleteHealthMonitor = createAsyncThunk(
  'user/deleteHealthMonitor',
  async ({ projectId, healthMonitorId }) => {
    try {
      const apiDeleteHealthMonitor = await apiEndPoints.deleteLoadHealthMonitor(
        projectId,
        healthMonitorId,
      );
      const response = await apiDeleteHealthMonitor.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deletePoolMember = createAsyncThunk(
  'user/deletePoolMember',
  async ({ projectId, data }) => {
    try {
      const apiDeletePoolMember = await apiEndPoints.deleteMember(
        projectId,
        data,
      );
      const response = await apiDeletePoolMember.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deletePool = createAsyncThunk(
  'user/deletePool',
  async ({ projectId, poolId }) => {
    try {
      const apiDeletePool = await apiEndPoints.deleteLoadPool(
        projectId,
        poolId,
      );
      const response = await apiDeletePool.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deleteSubnetPort = createAsyncThunk(
  'user/deleteSubnetPort',
  async ({ projectId, portId }) => {
    try {
      const apiDeletePort = await apiEndPoints.deletePort(projectId, portId);
      const response = await apiDeletePort.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deleteFloatingIp = createAsyncThunk(
  'user/deleteFloatingIp',
  async ({ projectId, floatingIpId }) => {
    try {
      const apiDeleteFloatingIp = await apiEndPoints.deleteFloatIp(
        projectId,
        floatingIpId,
      );
      const response = await apiDeleteFloatingIp.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deleteNetworkInterface = createAsyncThunk(
  'user/deleteNetworkInterface',
  async ({ projectId, data }) => {
    try {
      const apiDeleteNetworkInterface = await apiEndPoints.deleteInterface(
        projectId,
        data,
      );
      const response = await apiDeleteNetworkInterface.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const stopUserServer = createAsyncThunk(
  'user/stopUserServer',
  async ({ projectId, serverId }) => {
    try {
      const apiStopServer = await apiEndPoints.stopServer(projectId, serverId);
      const response = await apiStopServer.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const restartUserServer = createAsyncThunk(
  'user/restartUserServer',
  async ({ projectId, serverId }) => {
    try {
      const apiRestartServer = await apiEndPoints.restartServer(
        projectId,
        serverId,
      );
      const response = await apiRestartServer.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const createPasswordResetLink = createAsyncThunk(
  'user/createPasswordResetLink',
  async ({ passwordResetFormData }, { rejectWithValue }) => {
    try {
      const apiPasswordResetLink = await apiEndPoints.passwordResetLink(
        passwordResetFormData,
      );
      const response = await apiPasswordResetLink.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createNewPassword = createAsyncThunk(
  'user/createNewPassword',
  async ({ newPasswordFormData }, { rejectWithValue }) => {
    try {
      const apiNewPasswordCreation =
        await apiEndPoints.setNewPassword(newPasswordFormData);
      const response = await apiNewPasswordCreation.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const updateUserProfile = createAsyncThunk(
  'user/updateUserProfile',
  async ({ updateUserProfile }, { rejectWithValue }) => {
    try {
      const apiUpdateUserProfile =
        await apiEndPoints.updateProfile(updateUserProfile);
      const response = await apiUpdateUserProfile.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const projectVolumeList = createAsyncThunk(
  'user/ProjectVolumeList',
  async (projectId) => {
    try {
      const apiProjectVolumeList = await apiEndPoints.volumeList(projectId);
      const response = await apiProjectVolumeList.data?.volumes;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const projectNetworkList = createAsyncThunk(
  'user/ProjectNetworkList',
  async (projectId) => {
    try {
      const apiProjectNetworkList = await apiEndPoints.networkList(projectId);
      const response = await apiProjectNetworkList.data?.networks;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const projectSubnetList = createAsyncThunk(
  'user/ProjectSubnetList',
  async ({ projectId, params }) => {
    console.log(params);
    try {
      const apiProjectSubnetList = await apiEndPoints.subnetList(
        projectId,
        params,
      );
      const response = await apiProjectSubnetList.data?.subnets;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const projectListenerList = createAsyncThunk(
  'user/ProjectListenerList',
  async ({ projectId, params }) => {
    try {
      const apiProjectSubnetList = await apiEndPoints.listenerList(
        projectId,
        params,
      );
      const response = await apiProjectSubnetList.data?.listeners;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const projectHealthMonitorList = createAsyncThunk(
  'user/ProjectHealthMonitorList',
  async ({ projectId, poolId }) => {
    try {
      const apiProjectHealthMonitorList = await apiEndPoints.healthMonitorList(
        projectId,
        poolId,
      );
      const response = await apiProjectHealthMonitorList.data?.healthmonitors;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const projectMemberList = createAsyncThunk(
  'user/ProjectMemberList',
  async ({ projectId, poolId }) => {
    try {
      const apiProjectMemberList = await apiEndPoints.memberList(
        projectId,
        poolId,
      );
      const response = await apiProjectMemberList.data?.members;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const projectPoolList = createAsyncThunk(
  'user/ProjectPoolList',
  async ({ projectId, params }) => {
    try {
      const apiProjectPoolList = await apiEndPoints.poolList(projectId, params);
      const response = await apiProjectPoolList.data?.pools;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const projectPortList = createAsyncThunk(
  'user/ProjectPortList',
  async ({ projectId, params }) => {
    try {
      const apiProjectPortList = await apiEndPoints.portList(projectId, params);
      const response = await apiProjectPortList.data?.ports;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const projectRouterList = createAsyncThunk(
  'user/ProjectRouterList',
  async (projectId) => {
    try {
      const apiProjectRouterList = await apiEndPoints.routerList(projectId);
      const response = await apiProjectRouterList.data?.routers;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const projectRouterInterfaceList = createAsyncThunk(
  'user/ProjectRouterInterfaceList',
  async ({ projectId, data }) => {
    try {
      const apiProjectRouterInterfaceList =
        await apiEndPoints.routerInterfaceList(projectId, data);
      const response = await apiProjectRouterInterfaceList.data?.interfaces;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const paymentHistoryList = createAsyncThunk(
  'user/paymentHistoryList',
  async () => {
    try {
      const apipaymentHistoryList = await apiEndPoints.paymentHistory();
      const response = await apipaymentHistoryList.data?.zippy_transactions;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const auditLogList = createAsyncThunk('user/auditLogList', async () => {
  try {
    const apiauditLogList = await apiEndPoints.getAuditLogs();
    const response = await apiauditLogList.data?.logs;
    return response;
  } catch (error) {
    return error.response.data;
  }
});

export const creditHistoryList = createAsyncThunk(
  'user/creditHistoryList',
  async () => {
    try {
      const apiCreditHistoryList = await apiEndPoints.paymentHistory();
      const response = await apiCreditHistoryList.data?.results;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const userResourceSubscriptionList = createAsyncThunk(
  'user/userResourceSubscriptionList',
  async () => {
    try {
      const apiuserResourceSubscriptionList =
        await apiEndPoints.userResourceSubscription();
      const response = await apiuserResourceSubscriptionList.data?.results;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const invoiceList = createAsyncThunk('user/invoiceList', async () => {
  try {
    const apiInvoiceList = await apiEndPoints.getInvoices();
    const response = await apiInvoiceList.data;
    return response;
  } catch (error) {
    return error.response.data;
  }
});

export const payInvoice = createAsyncThunk(
  'user/payInvoice',
  async ({ payInvoiceFormData }, { rejectWithValue }) => {
    try {
      const apiPayInvoice = await apiEndPoints.payInvoice(payInvoiceFormData);
      const response = await apiPayInvoice.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createProjectVolumes = createAsyncThunk(
  'user/createVolume',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiCreateProjectVolume = await apiEndPoints.createVolume(
        data,
        projectId,
      );
      const response = await apiCreateProjectVolume.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createWhiteLabel = createAsyncThunk(
  'user/createWhiteLabel',
  async (whiteLabelData, { rejectWithValue }) => {
    try {
      const response = await apiEndPoints.registerWhiteLabel(whiteLabelData);
      return response.data;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const listPartners = createAsyncThunk('user/ListPartners', async () => {
  try {
    const apiListPartners = await apiEndPoints.getPartners();
    const response = (await apiListPartners.data?.partners) || [];
    return response;
  } catch (error) {
    return error.response.data;
  }
});

export const listPartner = createAsyncThunk(
  'user/ListPartner',
  async (partnerId) => {
    try {
      const apiListPartner = await apiEndPoints.getPartner(partnerId);
      const response = (await apiListPartner.data) || [];
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const createProjectNetworks = createAsyncThunk(
  'user/createNetwork',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiCreateProjectNetwork = await apiEndPoints.createNetwork(
        data,
        projectId,
      );
      const response = await apiCreateProjectNetwork.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const allocateProjectFloatingIPs = createAsyncThunk(
  'user/allocateProjectFloatingIPs',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiAllocateProjectFloatingIPs =
        await apiEndPoints.allocateFloatingIPS(data, projectId);
      const response = await apiAllocateProjectFloatingIPs.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createProjectRouters = createAsyncThunk(
  'user/createRouter',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiCreateProjectRouter = await apiEndPoints.createRouter(
        data,
        projectId,
      );
      const response = await apiCreateProjectRouter.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const listProjectRouterInterfaces = createAsyncThunk(
  'user/createRouter',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiCreateProjectRouter = await apiEndPoints.createRouter(
        data,
        projectId,
      );
      const response = await apiCreateProjectRouter.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const listProjectLoadBalancers = createAsyncThunk(
  'user/listProjectLoadBalancers',
  async (projectId, { rejectWithValue }) => {
    try {
      const apiListProjectLoadBalancers =
        await apiEndPoints.getLoadBalancers(projectId);
      const response = await apiListProjectLoadBalancers.data?.loadbalancers;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createNetworkPorts = createAsyncThunk(
  'user/createNetworkPorts',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiCreateNetworkPorts = await apiEndPoints.createNetworkPort(
        data,
        projectId,
      );
      const response = await apiCreateNetworkPorts.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const updateProjectNetwork = createAsyncThunk(
  'user/updateProjectNetwork',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiUpdateProjectNetwork = await apiEndPoints.updateNetwork(
        data,
        projectId,
      );
      const response = await apiUpdateProjectNetwork.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const updateProjectRouter = createAsyncThunk(
  'user/updateProjectRouter',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiUpdateProjectRouter = await apiEndPoints.updateRouter(
        data,
        projectId,
      );
      const response = await apiUpdateProjectRouter.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const updateNetworkPort = createAsyncThunk(
  'user/updateNetworkPort',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiUpdateNetworkPort = await apiEndPoints.updateNetworkPort(
        data,
        projectId,
      );
      const response = await apiUpdateNetworkPort.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const updateFloatingIp = createAsyncThunk(
  'user/updateFloatingIp',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiUpdateFloatingIp = await apiEndPoints.updateFloatingIp(
        data,
        projectId,
      );
      const response = await apiUpdateFloatingIp.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const updateSubnet = createAsyncThunk(
  'user/updateSubnet',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiUpdateSubnet = await apiEndPoints.updateSubnet(data, projectId);
      const response = await apiUpdateSubnet.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const updateVolumeSnapshot = createAsyncThunk(
  'user/updateVolumeSnapshot',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiUpdateSubnet = await apiEndPoints.updateSnapshot(
        data,
        projectId,
      );
      const response = await apiUpdateSubnet.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const updateServerSnapshot = createAsyncThunk(
  'user/updateServerSnapshot',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiUpdateServerSnapshot =
        await apiEndPoints.updateSnapshotForServer(data, projectId);
      const response = await apiUpdateServerSnapshot.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createNetworkSubnet = createAsyncThunk(
  'user/createNetworkSubnet',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiCreateProjectNetwork = await apiEndPoints.createSubnet(
        data,
        projectId,
      );
      const response = await apiCreateProjectNetwork.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createNetworkInterface = createAsyncThunk(
  'user/createNetworkInterface',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiCreateInterface = await apiEndPoints.createInterface(
        data,
        projectId,
      );
      const response = await apiCreateInterface.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createLoadBalancer = createAsyncThunk(
  'user/createLoadBalancer',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.project_id;
      const apiCreateLoadBalancer = await apiEndPoints.createLoadBalancers(
        data,
        projectId,
      );
      const response = await apiCreateLoadBalancer.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createLoadListener = createAsyncThunk(
  'user/createLoadListener',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiCreateLoadListener = await apiEndPoints.createListeners(
        data,
        projectId,
      );
      const response = await apiCreateLoadListener.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const updateLoadListener = createAsyncThunk(
  'user/updateLoadListener',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiUpdateLoadListener = await apiEndPoints.updateListener(
        data,
        projectId,
      );
      const response = await apiUpdateLoadListener.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createLoadPool = createAsyncThunk(
  'user/createLoadPool',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiCreateLoadPool = await apiEndPoints.createPool(data, projectId);
      const response = await apiCreateLoadPool.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const updateLoadPool = createAsyncThunk(
  'user/updateLoadPool',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiUpdateLoadPool = await apiEndPoints.updatePool(data, projectId);
      const response = await apiUpdateLoadPool.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createHealthMonitor = createAsyncThunk(
  'user/createHealthMonitor',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiCreateHealthMonitor = await apiEndPoints.createHealthMonitors(
        data,
        projectId,
      );
      const response = await apiCreateHealthMonitor.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const createPoolMember = createAsyncThunk(
  'user/createPoolMember',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiCreatePoolMember = await apiEndPoints.createMember(
        data,
        projectId,
      );
      const response = await apiCreatePoolMember.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const updateHealthMonitor = createAsyncThunk(
  'user/updateHealthMonitor',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiUpdateHealthMonitor = await apiEndPoints.updateHealthMonitors(
        data,
        projectId,
      );
      const response = await apiUpdateHealthMonitor.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const updatePoolMember = createAsyncThunk(
  'user/updatePoolMember',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const apiUpdatePoolMember = await apiEndPoints.updateMember(
        data,
        projectId,
      );
      const response = await apiUpdatePoolMember.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const attachedVolumeToInstance = createAsyncThunk(
  'user/createVolume',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const serverId = data.serverId;
      const apiAttachToInstance = await apiEndPoints.attachInstance(
        data,
        projectId,
        serverId,
      );
      const response = await apiAttachToInstance.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);
export const detachedVolumeToInstance = createAsyncThunk(
  'user/createVolume',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const serverId = data.server_id;
      const volumeId = data.volume_id;
      const apiAttachToInstance = await apiEndPoints.detachInstance(
        projectId,
        serverId,
        volumeId,
      );
      const response = await apiAttachToInstance.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);
export const createPaymentLink = createAsyncThunk(
  'user/payment',
  async (paymentData, { rejectWithValue }) => {
    try {
      const paymentLinkAPI =
        await apiEndPoints.generatePaymentLink(paymentData);
      const response = await paymentLinkAPI.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const deleteVolumeFromInstance = createAsyncThunk(
  'user/deleteVolumeFromInstance',
  async (data, { rejectWithValue }) => {
    try {
      const projectId = data.projectId;
      const volumeId = data.volumeId;
      const apiAttachToInstance = await apiEndPoints.deleteVolumeFromInstance(
        projectId,
        volumeId,
      );
      const response = await apiAttachToInstance.data;
      return response;
    } catch (error) {
      return rejectWithValue({ data: error.response.data });
    }
  },
);

export const deleteVolumeImage = createAsyncThunk(
  'user/deleteVolumeImage',
  async ({ projectId, snapshotId }) => {
    try {
      const apiDeleteVolumeImage = await apiEndPoints.deleteImageFromVolume(
        projectId,
        snapshotId,
      );
      const response = await apiDeleteVolumeImage.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const restoreVolumeBackup = createAsyncThunk(
  'user/restoreVolumeBackup',
  async ({ data, projectId }) => {
    console.log(data);
    try {
      const apiRestoreVolumeBackup = await apiEndPoints.restoreBackupFromVolume(
        data,
        projectId,
      );
      const response = await apiRestoreVolumeBackup.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deleteVolumeBackup = createAsyncThunk(
  'user/deleteVolumeBackup',
  async ({ projectId, backupId }) => {
    try {
      const apiDeleteVolumeBackup = await apiEndPoints.deleteBackupFromVolume(
        projectId,
        backupId,
      );
      const response = await apiDeleteVolumeBackup.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deleteVolumeSnapshot = createAsyncThunk(
  'user/deleteVolumeSnapshot',
  async ({ projectId, snapshotId }) => {
    try {
      const apiDeleteVolumeSnapshot = await apiEndPoints.deleteSnapshot(
        projectId,
        snapshotId,
      );
      const response = await apiDeleteVolumeSnapshot.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const deleteServerSnapshot = createAsyncThunk(
  'user/deleteServerSnapshot',
  async ({ projectId, snapshotId }) => {
    try {
      const apiDeleteServerSnapshot = await apiEndPoints.deleteServerSnapshot(
        projectId,
        snapshotId,
      );
      const response = await apiDeleteServerSnapshot.data;
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

export const createSecret = createAsyncThunk(
  'user/createSecret',
  async ({ projectId, secretData }, { rejectWithValue }) => {
    try {
      const response = await apiEndPoints.createSecret(projectId, secretData);
      return response.data;
    } catch (error) {
      return rejectWithValue({ data: error.response?.data });
    }
  },
);

export const getSecrets = createAsyncThunk(
  'user/getSecrets',
  async (projectId) => {
    try {
      const response = await apiEndPoints.getSecrets(projectId);
      return response.data?.secrets || [];
    } catch (error) {
      return error.response?.data;
    }
  },
);

export const deleteSecret = createAsyncThunk(
  'user/deleteSecret',
  async ({ projectId, secretId }, { rejectWithValue }) => {
    try {
      const response = await apiEndPoints.deleteSecret(projectId, secretId);
      return response.data;
    } catch (error) {
      return rejectWithValue({ data: error.response?.data });
    }
  },
);

export const createSecretContainer = createAsyncThunk(
  'user/createSecretContainer',
  async ({ projectId, containerData }, { rejectWithValue }) => {
    try {
      const response = await apiEndPoints.createSecretContainer(
        projectId,
        containerData,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({ data: error.response?.data });
    }
  },
);

export const getSecretContainers = createAsyncThunk(
  'user/getSecretContainers',
  async (projectId) => {
    try {
      const response = await apiEndPoints.getSecretContainers(projectId);
      return response.data?.containers || [];
    } catch (error) {
      return error.response?.data;
    }
  },
);

export const deleteSecretContainer = createAsyncThunk(
  'user/deleteSecretContainer',
  async ({ projectId, containerId }, { rejectWithValue }) => {
    try {
      const response = await apiEndPoints.deleteSecretContainer(
        projectId,
        containerId,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({ data: error.response?.data });
    }
  },
);

export const addSecretToContainer = createAsyncThunk(
  'user/addSecretToContainer',
  async ({ projectId, containerId, data }, { rejectWithValue }) => {
    try {
      const response = await apiEndPoints.addSecretToContainer(
        projectId,
        containerId,
        data,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({ data: error.response?.data });
    }
  },
);

export const removeSecretFromContainer = createAsyncThunk(
  'user/removeSecretFromContainer',
  async ({ projectId, containerId, data }, { rejectWithValue }) => {
    try {
      const response = await apiEndPoints.removeSecretFromContainer(
        projectId,
        containerId,
        data,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({ data: error.response?.data });
    }
  },
);

export const switchCurrency = createAsyncThunk(
  'user/switchCurrency',
  async (data) => {
    try {
      const response = await apiEndPoints.switchCurrency(data);
      return response;
    } catch (error) {
      return error.response.data;
    }
  },
);

const logOutSession = () => {
  removeFromLocalStorage('user');
  removeFromLocalStorage('userSessionData');
  removeFromLocalStorage('userProjectList');
  removeFromLocalStorage('selectedProject');
};

const userSlice = createSlice({
  name: 'user',
  reducers: {
    setSelectedProject(state, action) {
      state.selectedProject = action.payload;
      saveToLocalStorage('selectedProject', JSON.stringify(action.payload));
    },
    setCurrency(state, action) {
      state.currency = action.payload;
      localStorage.setItem('currency', action.payload);
    },
    userClearLogOut(state) {
      state.isAuthenticated = false; // Set user to logged out
      state.selectedProject = null;
      state.userProjectList = [];
      removeFromLocalStorage('selectedProject');
      sessionStorage.clear();
      localStorage.clear();
      
    },
  },
  initialState: initialState,
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.fulfilled, (state, action) => {
        if (action.payload.statuscode === '00') {
          state.users = action.payload;
          showSuccessMessage(
            'Registration successful! We’ve sent you a confirmation email — please verify your email to get started.',
          );
        } else {
          state.error = extractErrorMessage(action.payload);
          showErrorMessage(action.payload);
        }
        state.loading = false;
      })
      .addCase(createUserVirtualAccount.fulfilled, (state, action) => {
        if (action.payload.status_code === '200') {
          state.users = action.payload;
        } else {
          state.error = action.payload.message;
          showErrorMessage(action.payload.message);
        }
        state.loading = false;
      })
      .addCase(userAuthenticate.fulfilled, (state, action) => {
        if (action.payload.statuscode === '00') {
          state.users = action.payload;
          state.isAuthenticated = true;
          state.userSessionData = action.payload;
        } else {
          state.error = extractErrorMessage(action.payload);
          showErrorMessage(action.payload);
        }
        state.loading = false;
      })
      .addCase(userLogOut.fulfilled, (state) => {
        state.selectedProject = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.users = null;
      })
      .addCase(listUserProjects.fulfilled, (state, action) => {
        if (action.payload.statuscode === '00') {
          state.users = action.payload;
          state.userProjectList = action.payload.projects;
        } else {
          state.error = action.payload.statusmessage;
          showErrorMessage(action.payload.statusmessage);
        }
        state.loading = false;
      })
      .addCase(updateUserProject.fulfilled, (state, action) => {
        if (action.payload.statuscode === '00') {
          state.users = action.payload;
          showSuccessMessage('Project details updated successfully');
        } else {
          state.error = action.payload.statusmessage;
          showErrorMessage(action.payload.statusmessage);
        }
        state.loading = false;
      })

      .addCase(listImages.fulfilled, (state, action) => {
        state.loading = false;
        state.images = action.payload;
      })
      .addCase(listImageDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.imageDetails = action.payload;
      })
      .addCase(listServerSnapshots.fulfilled, (state, action) => {
        state.loading = false;
        state.serverSnapshots = action.payload;
      })
      .addCase(listVolumeSnapshots.fulfilled, (state, action) => {
        state.loading = false;
        state.volumeSnapshots = action.payload;
      })
      .addCase(listVolumeBackups.fulfilled, (state, action) => {
        state.loading = false;
        state.volumeBackups = action.payload;
      })
      .addCase(listKeypairs.fulfilled, (state, action) => {
        state.loading = false;
        state.keypairs = action.payload;
      })
      .addCase(getUserVirtualAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.virtualAccount = action.payload;
      })
      .addCase(listSecurityGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.keypairs = action.payload;
      })

      .addCase(userAuthenticate.rejected, (state, action) => {
        state.loading = false;
        state.users = null;
        state.error =
          action.payload?.statusmessage ||
          action.error?.message ||
          'Unable to sign in.';
      })

      //Fulfilled with notification message
      .addMatcher(
        isAnyOf(
          createProject.fulfilled,
          createKeypair.fulfilled,
          createServerSnapshot.fulfilled,
          createSecret.fulfilled,
          deleteSecret.fulfilled,
          createSecretContainer.fulfilled,
          deleteSecretContainer.fulfilled,
          addSecretToContainer.fulfilled,
          removeSecretFromContainer.fulfilled,
          createVolumeSnapshot.fulfilled,
          createVolumeFromSnapshot.fulfilled,
          createBackup.fulfilled,
          resizeServer.fulfilled,
          confirmResizeServer.fulfilled,
          stopResizeServer.fulfilled,
          createSessionVirtualAccount.fulfilled,
          createContactSupport.fulfilled,
          createSecurityGroup.fulfilled,
          createSecurityGroupRule.fulfilled,
          // deleteSecurityGroup and deleteSecurityGroupRule handled in components to avoid duplicate messages
          // deleteSecurityGroup.fulfilled,
          // deleteSecurityGroupRule.fulfilled,
          createSecurityGroupInstance.fulfilled,
          deleteUserProject.fulfilled,
          deleteUserServer.fulfilled,
          deleteProjectNetwork.fulfilled,
          deleteProjectRouter.fulfilled,
          deleteSubnet.fulfilled,
          deleteProjectLoadBalancer.fulfilled,
          deleteListener.fulfilled,
          deleteHealthMonitor.fulfilled,
          deletePoolMember.fulfilled,
          deleteSubnetPort.fulfilled,
          deleteFloatingIp.fulfilled,
          deleteVolumeImage.fulfilled,
          restoreVolumeBackup.fulfilled,
          deleteVolumeBackup.fulfilled,
          deleteVolumeSnapshot.fulfilled,
          deleteServerSnapshot.fulfilled,
          deleteNetworkInterface.fulfilled,
          stopUserServer.fulfilled,
          restartUserServer.fulfilled,
          createPasswordResetLink.fulfilled,
          payInvoice.fulfilled,
          createNewPassword.fulfilled,
          createProjectVolumes.fulfilled,
          createProjectNetworks.fulfilled,
          createWhiteLabel.fulfilled,
          allocateProjectFloatingIPs.fulfilled,
          createProjectRouters.fulfilled,
          createNetworkPorts.fulfilled,
          updateProjectNetwork.fulfilled,
          updateNetworkPort.fulfilled,
          updateFloatingIp.fulfilled,
          updateSubnet.fulfilled,
          updateVolumeSnapshot.fulfilled,
          updateServerSnapshot.fulfilled,
          updateLoadListener.fulfilled,
          updateLoadPool.fulfilled,
          updateHealthMonitor.fulfilled,
          updatePoolMember.fulfilled,
          updateProjectRouter.fulfilled,
          createNetworkSubnet.fulfilled,
          createNetworkInterface.fulfilled,
          createLoadBalancer.fulfilled,
          createLoadListener.fulfilled,
          createLoadPool.fulfilled,
          createHealthMonitor.fulfilled,
          createPoolMember.fulfilled,
          attachedVolumeToInstance.fulfilled,
          createPaymentLink.fulfilled,
          deleteVolumeFromInstance.fulfilled,
        ),
        (state, action) => {
          const payload = action.payload || {};
          const responseData = payload.data || payload;
          const statuscode = responseData?.statuscode || responseData?.status_code || payload?.statuscode;
          const statusmessage = responseData?.statusmessage || responseData?.message || payload?.statusmessage;

          if (statuscode === '00' || statuscode === 200) {
            state.users = responseData;
            showSuccessMessage(statusmessage);
          } else if (statuscode && statuscode !== '00' && payload.data) {
            // Handle specific cases where non-"00" statuscode still provides valid user data
            state.users = payload.data;
            // Only show success message if explicitly indicated by the API
            if (statusmessage && !payload.error) {
              showSuccessMessage(statusmessage);
            }
          } else {
            state.error = extractErrorMessage(payload);
            showErrorMessage(payload);
          }
          state.loading = false;
        },
      )

      //Fulfilled without notification message
      .addMatcher(
        isAnyOf(
          createServer.fulfilled,
          setPartnerFlavorPrices.fulfilled,
          listDews.fulfilled,
          listPartnerDews.fulfilled,
          listServers.fulfilled,
          listPartners.fulfilled,
          listPartner.fulfilled,
          listDBServers.fulfilled,
          listProjectFloatingIPs.fulfilled,
          listServer.fulfilled,
          getDBServerStatus.fulfilled,
          listFloatingIP.fulfilled,
          listPartnerResources.fulfilled,
          listPartnerPriceLists.fulfilled,
          listPartnerWallet.fulfilled,
          listNetwork.fulfilled,
          listLoadBalancer.fulfilled,
          listLoadListener.fulfilled,
          listLoadPool.fulfilled,
          listNetworkSubnet.fulfilled,
          listRouter.fulfilled,
          listServerSnapshots.fulfilled,
          listVolumeSnapshots.fulfilled,
          listVolumeBackups.fulfilled,
          getUserVirtualAccount.fulfilled,
          getCreditValidator.fulfilled,
          switchUserProject.fulfilled,
          getSingleSecurityGroup.fulfilled,
          deleteSecurityGroup.fulfilled,
          deleteSecurityGroupRule.fulfilled,
          getResourceStatus.fulfilled,
          projectVolumeList.fulfilled,
          projectNetworkList.fulfilled,
          projectSubnetList.fulfilled,
          projectListenerList.fulfilled,
          projectHealthMonitorList.fulfilled,
          projectMemberList.fulfilled,
          projectPoolList.fulfilled,
          projectPortList.fulfilled,
          projectRouterList.fulfilled,
          projectRouterInterfaceList.fulfilled,
          listProjectLoadBalancers.fulfilled,
          invoiceList.fulfilled,
          creditHistoryList.fulfilled,
          paymentHistoryList.fulfilled,
          auditLogList.fulfilled,
          switchCurrency.fulfilled,
        ),
        (state, action) => {
          // Handle both old and new payload structures
          const responseData = action.payload?.data || action.payload;
          const statuscode = responseData?.statuscode || responseData?.status_code;
          
          // Check if statuscode indicates an error even if HTTP status is 200
          if (statuscode && statuscode !== '00' && statuscode !== 200 && statuscode !== 201 && statuscode !== 204) {
            state.error = extractErrorMessage(responseData);
            showErrorMessage(responseData);
            state.loading = false;
            return;
          }

          if (
            responseData?.statuscode === '00' ||
            action.payload?.status === 200 ||
            action.payload?.status === 204 ||
            action.payload?.status === 201
          ) {
            if (responseData) {
              state.users = responseData;
            }
          }
          state.loading = false;
        },
      )
      .addMatcher(
        isAnyOf(
          registerUser.pending,
          userAuthenticate.pending,
          listDews.pending,
          listPartnerDews.pending,
          listUserProjects.pending,
          updateUserProject.pending,
          createProject.pending,
          listImages.pending,
          listImageDetails.pending,
          listKeypairs.pending,
          createKeypair.pending,
          createServer.pending,
          setPartnerFlavorPrices.pending,
          listServers.pending,
          listPartners.pending,
          listPartner.pending,
          listPartners.pending,
          listDBServers.pending,
          // getDBServerStatus.pending,
          listFloatingIP.pending,
          listPartnerResources.pending,
          listPartnerPriceLists.pending,
          listPartnerWallet.pending,
          listNetwork.pending,
          listLoadBalancer.pending,
          listLoadListener.pending,
          listLoadPool.pending,
          listNetworkSubnet.pending,
          listRouter.pending,
          listServerSnapshots.pending,
          listVolumeSnapshots.pending,
          listVolumeBackups.pending,
          createServerSnapshot.pending,
          createSecret.pending,
          deleteSecret.pending,
          createSecretContainer.pending,
          deleteSecretContainer.pending,
          addSecretToContainer.pending,
          removeSecretFromContainer.pending,
          createVolumeSnapshot.pending,
          createVolumeFromSnapshot.pending,
          createBackup.pending,
          resizeServer.pending,
          confirmResizeServer.pending,
          stopResizeServer.pending,
          createUserVirtualAccount.pending,
          createSessionVirtualAccount.pending,
          getUserVirtualAccount.pending,
          getCreditValidator.pending,
          createContactSupport.pending,
          createSecurityGroup.pending,
          createSecurityGroupRule.pending,
          listSecurityGroups.pending,
          switchUserProject.pending,
          deleteSecurityGroup.pending,
          getSingleSecurityGroup.pending,
          deleteSecurityGroupRule.pending,
          createSecurityGroupInstance.pending,
          getResourceStatus.pending,
          deleteUserProject.pending,
          deleteUserServer.pending,
          deleteProjectNetwork.pending,
          deleteProjectRouter.pending,
          deleteSubnet.pending,
          deleteProjectLoadBalancer.pending,
          deleteListener.pending,
          deleteHealthMonitor.pending,
          deletePoolMember.pending,
          deleteSubnetPort.pending,
          deleteFloatingIp.pending,
          deleteVolumeImage.pending,
          restoreVolumeBackup.pending,
          deleteVolumeBackup.pending,
          deleteVolumeSnapshot.pending,
          deleteServerSnapshot.pending,
          deleteNetworkInterface.pending,
          restartUserServer.pending,
          stopUserServer.pending,
          createPasswordResetLink.pending,
          payInvoice.pending,
          createNewPassword.pending,
          projectVolumeList.pending,
          projectNetworkList.pending,
          projectSubnetList.pending,
          projectListenerList.pending,
          projectHealthMonitorList.pending,
          projectMemberList.pending,
          projectPoolList.pending,
          projectPortList.pending,
          projectRouterList.pending,
          projectRouterInterfaceList.pending,
          listProjectLoadBalancers.pending,
          invoiceList.pending,
          creditHistoryList.pending,
          paymentHistoryList.pending,
          auditLogList.pending,
          createProjectVolumes.pending,
          createProjectNetworks.pending,
          createWhiteLabel.pending,
          allocateProjectFloatingIPs.pending,
          createProjectRouters.pending,
          createNetworkPorts.pending,
          updateProjectNetwork.pending,
          updateNetworkPort.pending,
          updateFloatingIp.pending,
          updateSubnet.pending,
          updateVolumeSnapshot.pending,
          updateServerSnapshot.pending,
          updateLoadListener.pending,
          updateLoadPool.pending,
          updateHealthMonitor.pending,
          updatePoolMember.pending,
          updateProjectRouter.pending,
          createNetworkSubnet.pending,
          createNetworkInterface.pending,
          createLoadBalancer.pending,
          createLoadListener.pending,
          createLoadPool.pending,
          createHealthMonitor.pending,
          createPoolMember.pending,
          attachedVolumeToInstance.pending,
          createPaymentLink.pending,
          deleteVolumeFromInstance.pending,
          switchCurrency.pending,
        ),
        (state) => {
          state.loading = true;
          state.users = null;
          state.error = null;
        },
      )
      .addMatcher(
        isAnyOf(
          registerUser.rejected,
          listDews.rejected,
          listPartnerDews.rejected,
          listUserProjects.rejected,
          updateUserProject.rejected,
          createProject.rejected,
          listImages.rejected,
          listImageDetails.rejected,
          listKeypairs.rejected,
          createKeypair.rejected,
          createServer.rejected,
          setPartnerFlavorPrices.rejected,
          listServers.rejected,
          listPartners.rejected,
          listPartner.rejected,
          listDBServers.rejected,
          listProjectFloatingIPs.rejected,
          listServer.rejected,
          getDBServerStatus.rejected,
          listFloatingIP.rejected,
          listPartnerResources.rejected,
          listPartnerPriceLists.rejected,
          listPartnerWallet.rejected,
          listNetwork.rejected,
          listLoadBalancer.rejected,
          listLoadListener.rejected,
          listLoadPool.rejected,
          listNetworkSubnet.rejected,
          listRouter.rejected,
          listServerSnapshots.rejected,
          listVolumeSnapshots.rejected,
          listVolumeBackups.rejected,
          createServerSnapshot.rejected,
          createSecret.rejected,
          deleteSecret.rejected,
          createSecretContainer.rejected,
          deleteSecretContainer.rejected,
          addSecretToContainer.rejected,
          removeSecretFromContainer.rejected,
          createVolumeSnapshot.rejected,
          createVolumeFromSnapshot.rejected,
          createBackup.rejected,
          resizeServer.rejected,
          confirmResizeServer.rejected,
          stopResizeServer.rejected,
          createUserVirtualAccount.rejected,
          createSessionVirtualAccount.rejected,
          getUserVirtualAccount.rejected,
          getCreditValidator.rejected,
          createContactSupport.rejected,
          createSecurityGroup.rejected,
          createSecurityGroupRule.rejected,
          listSecurityGroups.rejected,
          createSecurityGroupRule.rejected,
          switchUserProject.rejected,
          deleteSecurityGroup.rejected,
          getSingleSecurityGroup.rejected,
          deleteSecurityGroupRule.rejected,
          createSecurityGroupInstance.rejected,
          getResourceStatus.rejected,
          deleteUserProject.rejected,
          deleteUserServer.rejected,
          deleteProjectNetwork.rejected,
          deleteProjectRouter.rejected,
          deleteSubnet.rejected,
          deleteProjectLoadBalancer.rejected,
          deleteListener.rejected,
          deleteHealthMonitor.rejected,
          deletePoolMember.rejected,
          deleteSubnetPort.rejected,
          restoreVolumeBackup.rejected,
          deleteVolumeBackup.rejected,
          deleteFloatingIp.rejected,
          deleteVolumeSnapshot.rejected,
          deleteServerSnapshot.rejected,
          deleteNetworkInterface.rejected,
          restartUserServer.rejected,
          stopUserServer.rejected,
          createPasswordResetLink.rejected,
          payInvoice.rejected,
          createNewPassword.rejected,
          projectVolumeList.rejected,
          projectNetworkList.rejected,
          projectSubnetList.rejected,
          projectListenerList.rejected,
          projectHealthMonitorList.rejected,
          projectMemberList.rejected,
          projectPoolList.rejected,
          projectPortList.rejected,
          projectRouterList.rejected,
          projectRouterInterfaceList.rejected,
          listProjectLoadBalancers.rejected,
          invoiceList.rejected,
          creditHistoryList.rejected,
          paymentHistoryList.rejected,
          auditLogList.rejected,
          createProjectVolumes.rejected,
          createProjectNetworks.rejected,
          createWhiteLabel.rejected,
          allocateProjectFloatingIPs.rejected,
          createProjectRouters.rejected,
          createNetworkPorts.rejected,
          updateProjectNetwork.rejected,
          updateNetworkPort.rejected,
          updateFloatingIp.rejected,
          updateSubnet.rejected,
          updateVolumeSnapshot.rejected,
          updateServerSnapshot.rejected,
          updateLoadListener.rejected,
          updateLoadPool.rejected,
          updateHealthMonitor.rejected,
          updatePoolMember.rejected,
          updateProjectRouter.rejected,
          createNetworkSubnet.rejected,
          createNetworkInterface.rejected,
          createLoadBalancer.rejected,
          createLoadListener.rejected,
          createLoadPool.rejected,
          createHealthMonitor.rejected,
          createPoolMember.rejected,
          attachedVolumeToInstance.rejected,
          createPaymentLink.rejected,
          deleteVolumeFromInstance.rejected,
          switchCurrency.rejected,
        ),
        (state, action) => {
          state.loading = false;
          state.users = null;
          const { data, navigate } = action.payload || {};
          // Pass the full error object or payload to showErrorMessage for robust extraction
          const errorObj = data || action.payload || action.error || {};

          let errorMessage =
            errorObj?.statusmessage ||
            
            errorObj?.message ||
            errorObj?.error ||
            'Failed, Try Again';

          if (errorMessage === 'Invalid token' && navigate) {
            navigate('/sign-out');
            state.error = 'Session timed out. Please sign in to continue.';
            showErrorMessage(state.error);
          } else {
            state.error = errorMessage;
            // showErrorMessage now uses extractErrorMessage internally to find the most specific message
            showErrorMessage(errorObj);
          }
        },
      );
  },

});
export const { setSelectedProject, setCurrency, userClearLogOut } =
  userSlice.actions;
export const userReducer = userSlice.reducer;
